import { Box, Container, Body, Divider } from '@hover/blueprint';
import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { IOpenRoles } from '../../../interfaces/IOpenRoles';
import { IJob } from '../../../interfaces/IJob';
import Header from './Header';
import List from './List';
import { useInterSectionObserver } from '../../../hooks';

const query = graphql`
  query OpenRoles {
    allHoverJobs {
      nodes {
        id
        title
        office
        department
        offices {
          id
          name
        }
        departments {
          id
          name
        }
      }
    }
    allHoverOffices {
      nodes {
        id
        name
      }
    }
    allHoverDepartments {
      nodes {
        id
        name
      }
    }
  }
`;

const filterJobs = (
  allJobs: IJob[],
  selectedOffice: string,
  selectedDepartment: string,
) =>
  allJobs.filter(({ office, department }) => {
    const officeFilter =
      selectedOffice === 'all locations' ? true : office === selectedOffice;

    const departmentFilter =
      selectedDepartment === 'all teams'
        ? true
        : department === selectedDepartment;

    return officeFilter && departmentFilter;
  });

interface Props {
  setCurrentSection: React.Dispatch<React.SetStateAction<string>>;
}

const OpenRoles: React.FC<Props> = ({ setCurrentSection }) => {
  const openRolesRef = useInterSectionObserver(
    isIntersecting => {
      if (isIntersecting) {
        setCurrentSection('Open Roles');
      }
    },
    {
      rootMargin: '0px',
      root: null,
      threshold: 0.1,
    },
  );

  const { allHoverDepartments, allHoverJobs, allHoverOffices }: IOpenRoles =
    useStaticQuery(query);

  const [selectedTeam, setSelectedTeam] = useState<string>('all teams');
  const [selectedLocation, setSelectedLocation] =
    useState<string>('all locations');

  const filteredJobs = filterJobs(
    allHoverJobs.nodes,
    selectedLocation,
    selectedTeam,
  );

  return (
    <Box
      id="open-roles"
      ref={openRolesRef.elementRef}
      w="100%"
      flexDir="column"
      bg="brandTan.100"
      alignItems="center"
      justifyContent="center"
      py={{ base: 700, desktop: 1000 }}
      pl={{ base: 0, desktop: '110px' }}
      position="relative"
    >
      <Container maxW="994px" px={500}>
        <Header
          offices={allHoverOffices.nodes}
          departments={allHoverDepartments.nodes}
          setSelectedTeam={setSelectedTeam}
          setSelectedLocation={setSelectedLocation}
        />
        <Body
          size={500}
          mb={900}
          textAlign={{ base: 'center', desktop: 'left' }}
        >
          {filteredJobs.length} roles across{' '}
          {`${selectedTeam === 'all teams' ? 'TEAMS' : selectedTeam}`} and{' '}
          {`${
            selectedTeam === 'all locations' ? 'LOCATIONS' : selectedLocation
          }`}
        </Body>
        {filteredJobs.length && (
          <Divider
            borderColor="neutral.500"
            my={0}
            borderWidth={0}
            borderTopWidth="1px"
          />
        )}
        <List openRoles={filteredJobs} />
      </Container>
    </Box>
  );
};

export default OpenRoles;
